/* eslint-disable max-lines */
import { CreateMaterialData } from '@redviking/argonaut-util/types/material-create-data';
import { Notify } from 'src/notifications';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { CreateMaterialDocument, type GetMaterialModelQuery, UpdateMaterialUdfsAndAttributesDocument } from 'types/db';
import { ArgoMaterialAttributeInsertInput } from '@redviking/argonaut-util/types/db-schema/argo/material_attribute';
import { type ArgoMaterialStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/material_status';
import { type ValidationResult } from 'types';
import { validateMaterial } from './material.validations';
import {
    type EntityModifyParameters,
    type ExtendedEntityParameters,
} from 'src/components/EntityDetail';
import type { UDFData } from 'src/udf/udf.validations';
import { v4 as uuidv4 } from 'uuid';

export type MaterialEntity = {
    id: string;
    enabled: boolean;
    createdBy: string;
    created_at?: string;
    attributeIds: string[];
    locationId: string | null;
    status: ArgoMaterialStatusEnum;
    snCfg: {
        kind: 'direct';
        serialNumber: string;
    } | {
        id: number;
        kind: 'fmt';
        name: string;
        tokens: {
            name: string;
            value: string;
        }[];
    };
    materialClass: {
        id: string;
        name: string;
    };
    materialModel: {
        id: string;
        model_number: string;
    };
    udfData: UDFData[];
    ignoreSchemaWarning: boolean;
    workOrderId?: string | null;
};

export const saveMaterialEntity = async (payload: ExtendedEntityParameters<'material'>): Promise<ValidationResult> => {
    const {
        entity,
        oldEntity: originalEntity,
    } = payload;
    const validationResult = await validateMaterial(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }

    const insert: ArgoMaterialAttributeInsertInput[] = entity.attributeIds.filter(newId => !originalEntity?.attributeIds.find(oldId => newId === oldId)).map(aId => ({
        attribute_id: aId,
        material_id: entity.id,
    }));
    const deleteIds = originalEntity?.attributeIds.filter(oldId => !entity.attributeIds.find(newId => newId === oldId)) || [];

    try {
        await gqlClient.request({
            document: UpdateMaterialUdfsAndAttributesDocument,
            variables: {
                attrsToAdd: insert,
                materialId: entity.id,
                attrsToDelete: deleteIds,
                materialUdfs: entity.udfData.map(udfDataInstance => ({
                    ...udfDataInstance,
                    material_id: entity.id,
                })),
            },
        });

        // NOTE: Status is NOT updated here. Material status should only be changed
        // through the MaterialStatusBtn component which uses the tRPC API endpoint.
        return {
            status: 'success',
        };
    } catch (error) {
        return {
            status: 'error',
            errorMessage: `Error saving material entity: ${error}`,
        };
    }
};

export async function createMaterialEntity (payload: EntityModifyParameters<'material'>): Promise<ValidationResult> {
    const { entity } = payload;
    const validationResult = await validateMaterial(entity);
    if (validationResult.status !== 'success') {
        return validationResult;
    }
    try {
        const matData: CreateMaterialData = {
            allowGet: false,
            status: entity.status,
            locationId: entity.locationId,
            attributeIds: entity.attributeIds,
            materialModelId: entity.materialModel.id,
            serialNumberCfg: entity.snCfg.kind === 'fmt'
                ? {
                    kind: 'fmt',
                    snfInputs: entity.snCfg.tokens.reduce((acc, cur) => {
                            acc[cur.name] = cur.value;
                            return acc;
                        }, {} as Record<string, string>),
                }
                : {
                    kind: 'direct',
                    serialNumber: entity.snCfg.serialNumber,
                },
            workOrderId: entity?.workOrderId || null,
            udfs: entity.udfData.reduce((acc, cur) => {
                if (typeof cur.num_val === 'number') {
                    acc[cur.udf_column_id] = cur.num_val;
                } else if (typeof cur.str_val === 'string') {
                    acc[cur.udf_column_id] = cur.str_val;
                }
                return acc;
            }, {} as Record<string, string | number>),
        };
        const {
            argo_create_material,
        } = await gqlClient.request({
            document: CreateMaterialDocument,
            variables: {
                args: {
                    create_material_data: matData,
                },
            },
        });
        const createdMaterialId = argo_create_material?.id;
        if (!createdMaterialId) {
            return {
                status: 'error',
                errorMessage: 'Error saving Material. No Material data returned',
            };
        }

        return {
            status: 'success',
            newRoute: {
                name: 'materialDetail',
                params: {
                    materialId: createdMaterialId,
                },
            },
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Material: "${err instanceof Error ? err.message : String(err)}"`,
        };
    }
}

export const applyMaterialModelDataToMaterialEntity = (materialEntity: MaterialEntity, materialModel: GetMaterialModelQuery['argo_material_model_by_pk'], wipeWorkOrderId: boolean = true): MaterialEntity => {
    if (!materialModel) {
        const errMsg = 'Model number not found when attempting to apply model data to material entity';
        Notify.error(errMsg);
        throw new Error(errMsg);
    }

    const newMaterialEntity: MaterialEntity = {
        ...materialEntity,
        materialModel: {
            ...materialEntity.materialModel,
            id: materialModel.id,
            model_number: materialModel.model_number,
        },
        materialClass: {
            ...materialEntity.materialClass,
            id: materialModel.material_class.id,
            name: materialModel.material_class.name,
        },
    };

    if (wipeWorkOrderId) {
        newMaterialEntity.workOrderId = null; // Clear the work order id if wipeWorkOrderId is true to ensure it does not persist when changing models, unless explicitly set otherwise.
    }

    const serialNumberFormat = materialModel.sn_format || materialModel.material_class.sn_format;

    // Apply the sn format to the material. Try to maintain the existing direct sn input if neither the previous nor the new model has a sn format.
    if (serialNumberFormat) {
        newMaterialEntity.snCfg = {
            kind: 'fmt',
            id: serialNumberFormat.id,
            name: serialNumberFormat.name,
            tokens: serialNumberFormat.config.tokens.filter(t => t.type === 'input').map(t => ({
                value: '',
                name: t.name,
            })),
        };
    } else if (newMaterialEntity.snCfg.kind === 'fmt') {
        newMaterialEntity.snCfg = {
            kind: 'direct',
            serialNumber: '',
        };
    }

    let newUdfs: UDFData[] = [];

    for (const uSchema of materialModel.material_class.material_udf_schemas) {
        newUdfs.push({
            id: uuidv4(),
            udf_column_id: uSchema.udf_column.id,
            str_val: uSchema.udf_column.data_type === 'text' ? '' : null,
            num_val: uSchema.udf_column.data_type === 'number' ? 0 : null,
        });
    }

    // Apply udf values to the material. Try to maintain the existing udf values if the new model has the same udf column(s).
    for (const classUdfDefaultValue of Object.values(materialModel.material_class.meta.udfDefaultData || {})) {
        const castedUdfItem = classUdfDefaultValue as UDFData;
        const existingUdfItem = newMaterialEntity.udfData.find(u => u.udf_column_id === castedUdfItem.udf_column_id);
        newUdfs = newUdfs.map(u => u.udf_column_id === castedUdfItem.udf_column_id ? existingUdfItem || castedUdfItem : u);
    }
    // NOTE: I am applying the model udf default values after the class default values to override
    for (const modelUdfDefaultValue of materialModel.material_udf_default_data) {
        const existingUdfItem = newMaterialEntity.udfData.find(u => u.udf_column_id === modelUdfDefaultValue.udf_column_id);
        newUdfs = newUdfs.map(u => u.udf_column_id === modelUdfDefaultValue.udf_column_id
            ? existingUdfItem || {
                ...u,
                num_val: modelUdfDefaultValue.num_val,
                str_val: modelUdfDefaultValue.str_val,
            }
            : u);
    }
    newMaterialEntity.udfData = newUdfs;

    // NOTE: We cannot apply any attribute data in this function since we cannot make assumptions on which attribute the attribute settings are referring to
    return newMaterialEntity;
};
